import React, { useState, useEffect } from "react";
import { Badge, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getTransactionLineMatches } from "src/docuclipper/api";

interface Props {
  invoiceTransactionLineId: number;
  refreshTrigger?: number;
}

interface POMatchStatus {
  hasMatch: boolean;
  matchCount: number;
  status: string;
  lastUpdated?: string;
}

export const InvoicePOMatchStatus: React.FC<Props> = ({
  invoiceTransactionLineId,
  refreshTrigger = 0,
}) => {
  const [loading, setLoading] = useState(true);
  const [matchStatus, setMatchStatus] = useState<POMatchStatus | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadMatchStatus();
  }, [invoiceTransactionLineId, refreshTrigger]);

  const loadMatchStatus = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getTransactionLineMatches(
        "invoice2po",
        invoiceTransactionLineId
      );

      const matches = response?.matches || [];
      setMatchStatus({
        hasMatch: matches.length > 0,
        matchCount: matches.length,
        status: matches.length > 0 ? "matched" : "no_match",
        lastUpdated: matches.length > 0 ? matches[0]?.matchedAt : undefined,
      });
    } catch (err) {
      console.error("Error loading PO match status:", err);
      setError("Failed to load PO match status");
      setMatchStatus({
        hasMatch: false,
        matchCount: 0,
        status: "no_match",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center">
        <Spinner animation="border" size="sm" className="mr-2" />
        <small className="text-muted">Loading PO match status...</small>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-danger">
        <FontAwesomeIcon icon="exclamation-triangle" className="mr-1" />
        <small>{error}</small>
      </div>
    );
  }

  if (!matchStatus) {
    return null;
  }

  const getStatusBadge = () => {
    if (matchStatus.hasMatch) {
      return (
        <Badge variant="success" className="mr-2">
          <FontAwesomeIcon icon="check" className="mr-1" />
          {matchStatus.matchCount} PO Match
          {matchStatus.matchCount > 1 ? "es" : ""}
        </Badge>
      );
    } else {
      return (
        <Badge variant="secondary" className="mr-2">
          <FontAwesomeIcon icon="times" className="mr-1" />
          No PO Match
        </Badge>
      );
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-between">
      <div>
        {getStatusBadge()}
        {matchStatus.lastUpdated && (
          <small className="text-muted">
            Updated: {new Date(matchStatus.lastUpdated).toLocaleString()}
          </small>
        )}
      </div>
    </div>
  );
};
